import { useState } from "react";

interface ToolLogoProps {
  name: string;
  logoUrl?: string;
  className?: string;
}

// Deterministic brand color per tool, picked from the design system's
// semantic palette so it works in both light and dark themes.
const BRAND_TOKENS = [
  { bg: "bg-primary/15", fg: "text-primary" },
  { bg: "bg-blue-500/15", fg: "text-blue-400" },
  { bg: "bg-amber-500/15", fg: "text-amber-400" },
  { bg: "bg-purple-500/15", fg: "text-purple-400" },
  { bg: "bg-pink-500/15", fg: "text-pink-400" },
  { bg: "bg-emerald-500/15", fg: "text-emerald-400" },
  { bg: "bg-cyan-500/15", fg: "text-cyan-400" },
  { bg: "bg-orange-500/15", fg: "text-orange-400" },
];

function colorFor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return BRAND_TOKENS[Math.abs(hash) % BRAND_TOKENS.length];
}

// Try the stored logo first; on failure, fall back through additional
// favicon providers before showing the colored initial avatar. We extract
// the host from the stored URL (Google/DDG favicon URLs both encode the
// domain) so the chain works for any provider.
function extractHostFromLogo(url: string): string | null {
  try {
    // Google: https://www.google.com/s2/favicons?domain=foo.com&sz=64
    const u = new URL(url);
    const dom = u.searchParams.get("domain");
    if (dom) return dom.replace(/^www\./, "");
    // DuckDuckGo: https://icons.duckduckgo.com/ip3/foo.com.ico
    const m = u.pathname.match(/\/ip3\/([^/]+?)\.ico$/);
    if (m) return m[1].replace(/^www\./, "");
    // Clearbit (legacy): https://logo.clearbit.com/foo.com
    if (u.hostname === "logo.clearbit.com") {
      return u.pathname.replace(/^\//, "").replace(/^www\./, "") || null;
    }
    return null;
  } catch {
    return null;
  }
}

const ToolLogo = ({ name, logoUrl, className = "w-9 h-9" }: ToolLogoProps) => {
  const [stage, setStage] = useState(0); // 0 = primary, 1 = ddg, 2 = google, 3 = avatar
  const { bg, fg } = colorFor(name);

  const host = logoUrl ? extractHostFromLogo(logoUrl) : null;
  const sources: string[] = [];
  if (logoUrl) sources.push(logoUrl);
  if (host) {
    sources.push(`https://icons.duckduckgo.com/ip3/${host}.ico`);
    sources.push(`https://www.google.com/s2/favicons?domain=${host}&sz=64`);
  }
  // Dedupe while preserving order so we don't refetch the same URL.
  const uniqueSources = Array.from(new Set(sources));

  if (uniqueSources.length > 0 && stage < uniqueSources.length) {
    return (
      <div className={`${className} rounded-lg bg-secondary flex items-center justify-center shrink-0 overflow-hidden`}>
        <img
          key={uniqueSources[stage]}
          src={uniqueSources[stage]}
          alt={`${name} logo`}
          loading="lazy"
          onError={() => setStage((s) => s + 1)}
          className="w-full h-full object-contain"
        />
      </div>
    );
  }

  return (
    <div
      className={`${className} rounded-lg ${bg} flex items-center justify-center text-sm font-mono font-bold ${fg} shrink-0`}
      aria-label={`${name} initial`}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

export default ToolLogo;
